import React from 'react';
import { z } from 'zod';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '../ui/button';
import { Edit } from 'lucide-react';

export interface InfoField {
  label: string;
  defaultValue?: string | File | null;
  placeholder?: string;
  type?: 'text' | 'email' | 'textarea' | 'url' | 'file' | 'custom';
  id: string;
  editable?: boolean;
  accept?: string;
  render?: () => React.ReactNode;
}

interface InformationSectionProps {
  title: string;
  fields: InfoField[];
  columns?: 1 | 2;
  className?: string;

  editable?: boolean;
  validationSchema?: z.ZodType<Record<string, any>>;
  onSave?: (values: Record<string, any>) => void | Promise<void>;
}

const buildInitialValues = (fields: InfoField[]) => {
  const initial: Record<string, string | File | null> = {};
  fields.forEach((field) => {
    initial[field.id] = field.defaultValue ?? (field.type === 'file' ? null : '');
  });
  return initial;
};

const getFieldError = (
  schema: z.ZodType<Record<string, any>> | undefined,
  key: string,
  values: Record<string, any>
) => {
  if (!schema) return undefined;

  const result = schema.safeParse(values);

  if (!result.success) {
    return result.error.flatten().fieldErrors[key]?.[0];
  }

  return undefined;
};

export const InformationSection = ({
  title,
  fields,
  columns = 2,
  className,
  editable = false,
  validationSchema,
  onSave,
}: InformationSectionProps) => {
  const fieldsSignature = React.useMemo(
    () =>
      fields
        .map(
          (field) =>
            `${field.id}::${field.label}::${field.type ?? 'text'}::${field.defaultValue ?? ''}`
        )
        .join('|'),
    [fields]
  );
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = React.useState(false);
  const [formValues, setFormValues] = React.useState<Record<string, string | File | null>>(() =>
    buildInitialValues(fields)
  );
  const previousFieldsSignatureRef = React.useRef(fieldsSignature);
  const fileInputRefs = React.useRef<Record<string, HTMLInputElement | null>>({});

  React.useEffect(() => {
    if (previousFieldsSignatureRef.current === fieldsSignature) return;

    previousFieldsSignatureRef.current = fieldsSignature;
    setFormValues(buildInitialValues(fields));
    setErrors({});
  }, [fields, fieldsSignature]);

  const handleChange = (id: string, value: string | File | null) => {
    setFormValues((prev) => {
      const nextValues = { ...prev, [id]: value };
      const error = getFieldError(validationSchema, id, nextValues);
      setErrors((prevErrors) => ({ ...prevErrors, [id]: error ?? '' }));

      return nextValues;
    });
  };

  const handleSave = async () => {
    const result = validationSchema?.safeParse(formValues);

    if (result && !result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      const mappedErrors: Record<string, string> = {};

      Object.keys(fieldErrors).forEach((key) => {
        mappedErrors[key] = fieldErrors[key]?.[0] ?? '';
      });

      setErrors(mappedErrors);
      return;
    }

    setErrors({});
    await onSave?.(result?.success ? { ...formValues, ...result.data } : formValues);
    setIsEditing(false);
  };

  const renderFieldValue = (field: InfoField) => {
    const value = formValues[field.id];
    const isFieldEditable = editable && field.editable !== false;
    const isDisabled = !isEditing || !isFieldEditable;

    if (field.type === 'custom') {
      return field.render?.() ?? null;
    }

    if (field.type === 'file') {
      const fileValue = value instanceof File ? value : null;
      const urlValue = typeof value === 'string' ? value : '';

      const isAudioField = field.accept?.includes('audio');
      const isVideoField = field.accept?.includes('video');
      const previewUrl = fileValue ? URL.createObjectURL(fileValue) : urlValue;
      const fileKindLabel = isAudioField ? 'Audio' : isVideoField ? 'Video' : 'Image';

      return (
        <>
          <div className="space-y-3 rounded-xl bg-[#F8F5FA] p-4">
            {previewUrl ? (
              isAudioField ? (
                <div className="space-y-3">
                  <audio controls className="w-full">
                    <source src={previewUrl} />
                  </audio>

                  <p className="text-sm text-[#1E0036] break-all">
                    {fileValue?.name ||
                      (typeof value === 'string' ? value.split('/').pop() : '') ||
                      'Audio file'}
                  </p>
                </div>
              ) : isVideoField ? (
                <div className="space-y-3">
                  <video controls className="max-h-80 w-full rounded-lg bg-black">
                    <source src={previewUrl} />
                  </video>

                  <p className="text-sm text-[#1E0036] break-all">
                    {fileValue?.name ||
                      (typeof value === 'string' ? value.split('/').pop() : '') ||
                      'Video file'}
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <img
                    src={previewUrl}
                    alt={field.label}
                    className="h-16 w-16 rounded-lg object-cover"
                  />

                  <p className="text-sm text-[#1E0036] break-all">
                    {fileValue?.name || 'Current image'}
                  </p>
                </div>
              )
            ) : (
              <p className="text-sm text-[#62556B]">No {fileKindLabel.toLowerCase()} selected</p>
            )}

            {isFieldEditable && isEditing && (
              <>
                {!previewUrl ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRefs.current[field.id]?.click()}
                  >
                    Choose {fileKindLabel}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleChange(field.id, null)}
                  >
                    ✕ Remove
                  </Button>
                )}

                <Input
                  ref={(element) => {
                    fileInputRefs.current[field.id] = element;
                  }}
                  type="file"
                  accept={field.accept}
                  className="hidden"
                  onChange={(event) => handleChange(field.id, event.target.files?.[0] ?? null)}
                />
              </>
            )}
          </div>

          {errors[field.id] && <p className="text-xs text-red-500">{errors[field.id]}</p>}
        </>
      );
    }

    if (field.type === 'textarea') {
      return (
        <>
          <Textarea
            id={field.id}
            disabled={isDisabled}
            value={typeof value === 'string' ? value : ''}
            placeholder={field.placeholder}
            onChange={(e) => handleChange(field.id, e.target.value)}
            className="bg-[#F8F5FA] border-none rounded-xl text-sm min-h-[120px] focus-visible:ring-1 focus-visible:ring-[#9275C9] resize-none"
          />
          {errors[field.id] && <p className="text-xs text-red-500">{errors[field.id]}</p>}
        </>
      );
    }

    return (
      <>
        <Input
          id={field.id}
          type={field.type ?? 'text'}
          disabled={isDisabled}
          value={typeof value === 'string' ? value : ''}
          placeholder={field.placeholder}
          onChange={(e) => handleChange(field.id, e.target.value)}
          className="bg-[#F8F5FA] border-none h-11 rounded-xl text-sm focus-visible:ring-1 focus-visible:ring-[#9275C9]"
        />
        {errors[field.id] && <p className="text-xs text-red-500">{errors[field.id]}</p>}
      </>
    );
  };

  return (
    <Card className={`border-[#F0EEF9] shadow-sm flex flex-col ${className}`}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-[#1E0036] font-bold text-[clamp(16px,1.4vw,18px)]">
          {title}
        </CardTitle>

        {editable && (
          <div className="flex gap-2">
            {!isEditing ? (
              <button
                type="button"
                onClick={() => setIsEditing(!isEditing)}
                className="flex items-center gap-2 p-2 rounded-md "
              >
                <Edit size={20} /> <p className="text-lg text-black">Edit</p>
              </button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setFormValues(buildInitialValues(fields));
                    setIsEditing(false);
                  }}
                >
                  Cancel
                </Button>
                <button
                  type="button"
                  className="bg-darkPurple hover:bg-darkPurple text-sm text-white px-3 py-2 rounded-md"
                  onClick={handleSave}
                >
                  Save
                </button>
              </>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4 flex-grow flex flex-col justify-between">
        <div className={columns === 2 ? 'grid gap-6 md:grid-cols-2' : 'space-y-4'}>
          {fields.map((field) => (
            <div
              key={field.id}
              className={
                field.type === 'textarea' || field.type === 'file'
                  ? 'col-span-full space-y-2'
                  : 'space-y-2'
              }
            >
              <Label htmlFor={field.id} className="text-[#62556B] text-[13px] font-medium ml-1">
                {field.label}
              </Label>
              {renderFieldValue(field)}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
